import { addBreadcrumb } from '@sentry/nuxt';

/**
 * `loaded` only means the bootstrap script arrived. Dixa fails silently when
 * the messenger token is invalid or the domain is not whitelisted, so whether
 * the chat actually works is decided at click time, not here.
 */
export type DixaStatus = 'pending' | 'loaded' | 'failed';

export const SUPPORT_EMAIL = 'hej@greenbow.dk';

/** How long to wait for the bootstrap script before giving up on the chat. */
const SCRIPT_TIMEOUT_MS = 4000;

/** How long to wait for the messenger to become visible after opening it. */
const MESSENGER_TIMEOUT_MS = 2000;

const POLL_MS = 100;

/** Smallest side, in px, an iframe must have to count as an open messenger. */
const MIN_MESSENGER_SIZE = 120;

/**
 * Load state of the Dixa bootstrap script, set by the dixa client plugin.
 */
export const useDixaStatus = () =>
  useState<DixaStatus>('dixa-status', () => 'pending');

/**
 * Is the Dixa messenger on screen? Dixa renders it in an iframe, which exists
 * in the DOM well before it is usable — a messenger that failed to initialise
 * stays collapsed at zero height, so measure the box rather than trust that
 * the element is present.
 */
const isMessengerVisible = (): boolean => {
  const frames = document.querySelectorAll<HTMLIFrameElement>(
    'iframe[src*="messenger.dixa.io"], iframe[id*="dixa"], iframe[name*="dixa"]'
  );

  return Array.from(frames).some((frame) => {
    const rect = frame.getBoundingClientRect();
    // Require a real panel, not a launcher stub or a tracking pixel.
    return (
      rect.width >= MIN_MESSENGER_SIZE && rect.height >= MIN_MESSENGER_SIZE
    );
  });
};

/**
 * Poll a condition until it holds or the timeout expires.
 */
const waitFor = (
  condition: () => boolean,
  timeoutMs: number
): Promise<boolean> => {
  if (condition()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let waited = 0;

    const poll = setInterval(() => {
      if (condition()) {
        clearInterval(poll);
        resolve(true);
        return;
      }

      waited += POLL_MS;
      if (waited >= timeoutMs) {
        clearInterval(poll);
        resolve(false);
      }
    }, POLL_MS);
  });
};

/**
 * Record which channel a support click ended up using. Dixa fails silently by
 * design, so without this the only symptom of a broken messenger is a button
 * that appears to do nothing.
 */
const logSupportChannel = (channel: 'dixa' | 'email', reason: string) => {
  addBreadcrumb({
    category: 'support',
    level: 'info',
    message: `customer support opened via ${channel}`,
    data: { reason }
  });

  if (import.meta.dev) {
    // eslint-disable-next-line no-console
    console.info(`[support] channel=${channel} reason=${reason}`);
  }
};

/**
 * Composable for opening the customer service channel.
 *
 * Prefers the Dixa chat, since that is the staffed channel, and falls back to
 * a mailto link whenever the messenger does not actually come up — a missing
 * token, a script that never loaded, or an init that failed silently. The user
 * is never left without a way to reach customer service.
 */
export const useCustomerSupport = () => {
  const status = useDixaStatus();
  const { t } = useI18n();
  const { $posthog } = useNuxtApp();

  const openMailFallback = (reason: string) => {
    const subject = encodeURIComponent(t('CustomerSupport.mailSubject'));
    const href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;

    logSupportChannel('email', reason);

    // An anchor click launches the mail handler more reliably across browsers
    // than assigning location.href, which some treat as a blocked navigation.
    const link = document.createElement('a');
    link.href = href;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  /**
   * Open a customer service conversation.
   * @param source - where the user started from, for analytics
   */
  const openCustomerSupport = async (source: string) => {
    const track = (channel: 'dixa' | 'email') =>
      $posthog?.()?.capture('customer_support_opened', { source, channel });

    const fallback = (reason: string) => {
      track('email');
      openMailFallback(reason);
    };

    try {
      if (status.value === 'failed') {
        fallback('dixa unavailable');
        return;
      }

      const scriptLoaded = await waitFor(
        () => status.value === 'loaded',
        SCRIPT_TIMEOUT_MS
      );

      if (!scriptLoaded) {
        fallback('bootstrap script never loaded');
        return;
      }

      const w = window as DixaWindow;
      w._dixa_?.invoke('startConversation');

      const opened = await waitFor(isMessengerVisible, MESSENGER_TIMEOUT_MS);

      if (!opened) {
        fallback('messenger never opened');
        return;
      }

      logSupportChannel('dixa', 'messenger opened');
      track('dixa');
    } catch (error) {
      // Never leave the user without a way to reach support.
      fallback(`threw: ${(error as Error)?.message ?? 'unknown error'}`);
    }
  };

  return {
    openCustomerSupport,
    dixaStatus: readonly(status)
  };
};
